import React, { useState, useCallback, useMemo } from "react";
import { strings } from "../resources/strings";
import { routes } from "../router/routes";
import { Link, useLocation } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useAgencyPermissionValidation } from "../context/AgencyPermissionValidationContext";

// TypeScript interfaces
interface Route {
  path: string;
  name: string;
}

interface AuthenticatorContext {
  authStatus: string;
  user?: any; // AWS Amplify user object structure varies
}

// Constants
const NAVBAR_CONFIG = {
  height: "h-20",
  colors: {
    background: "bg-primary",
    text: "text-white",
    accent: "text-navbar-accent",
    border: "border-navbar-accent",
  },
  spacing: {
    buttonWidth: "min-w-42 w-auto", // Responsive width with 168px minimum
    buttonHeight: "h-11",
    buttonPadding: "py-2 px-4", // 8px top/bottom, 16px left/right (close to 15px)
    borderRadius: "rounded-lg",
    navGap: "gap-14", // Consistent gap between navigation items (56px)
  },
  typography: {
    size: "text-2xl",
    weight: {
      normal: "font-normal",
      bold: "font-bold",
    },
    lineHeight: "leading-none",
    tracking: "tracking-normal",
  },
  transitions: "transition-colors duration-200",
  shadow: "shadow-navbar",
} as const;

const MOBILE_DRAWER_CONFIG = {
  width: "w-64",
  background: "bg-primary",
  divider: "divide-y-2 divide-white/20",
} as const;

const SIGN_OUT_BUTTON_CONFIG = {
  width: "w-32",
  height: "h-12",
  minWidth: "min-w-25",
  borderRadius: "rounded-xl",
  border: "border border-primary",
  background: "bg-navbar-secondary",
  textColor: "text-primary",
  fontSize: "text-sm",
  fontWeight: "font-medium",
  shadow: "shadow-sign-out",
  hoverShadow: "hover:shadow-sign-out-hover",
} as const;

const NavBar: React.FC = () => {
  const [sideMenuExpanded, setSideMenuExpanded] = useState<boolean>(false);
  const currentLocation = useLocation();

  const { canAccessParticipantWebapp, loadingAgency } =
    useAgencyPermissionValidation();

  const { authStatus } = useAuthenticator((context: AuthenticatorContext) => [
    context.authStatus,
  ]);
  const isAuthenticated = useCallback(
    (authStatus: string): boolean => authStatus === "authenticated",
    [],
  );
  const { signOut } = useAuthenticator((context: AuthenticatorContext) => [
    context.user,
  ]);

  // Memoized route arrays to prevent recreation on every render
  const navHeaderRoutes = useMemo<Route[]>(
    () => [
      routes.dashboard,
      routes.appointments,
      routes.documents,
      routes.termsOfUse,
      routes.officerProfile,
    ],
    [],
  );

  const sideMenuRoutes = useMemo<Route[]>(
    () => [
      routes.dashboard,
      routes.appointments,
      routes.documents,
      routes.termsOfUse,
      routes.officerProfile,
    ],
    [],
  );

  // Memoized event handlers for performance
  const toggleSideMenu = useCallback(() => {
    setSideMenuExpanded((prev) => !prev);
  }, []);

  const handleSignOut = useCallback(() => {
    signOut();

    /**
     * This is a workaround to prevent an issue we are experiencing with the AWS Amplify
     * Authenticator component.
     *
     * The issue is reproduced via:
     * - sign in, refresh, and then sign out
     * - the user is redirected to the login page, but the form input fields are
     *   not properly displayed. (Shows "Username" input instead of "Phone Number")
     *
     * After reloading, the form input fields are displayed correctly.
     */
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  }, [signOut]);

  // Function to render the top header navigation buttons
  const configureNavigationHeader = useCallback(() => {
    return navHeaderRoutes.map((item: Route, index: number) => {
      const isActive = currentLocation.pathname === item.path;
      return (
        <Link
          to={item.path}
          key={index}
          className={`inline-block ${isActive ? "border-b-3 border-navbar-accent" : ""}`}
          aria-current={isActive ? "page" : undefined}
        >
          <button
            className={`
              ${NAVBAR_CONFIG.spacing.buttonWidth} 
              ${NAVBAR_CONFIG.spacing.buttonHeight} 
              ${NAVBAR_CONFIG.spacing.borderRadius} 
              ${NAVBAR_CONFIG.spacing.buttonPadding} 
              flex items-center justify-center 
              ${NAVBAR_CONFIG.colors.text} 
                             hover:bg-white/10 
               focus:outline-none 
               focus-visible:bg-white/10 
               focus-visible:ring-2 
               focus-visible:ring-white/50 
              normal-case 
              ${NAVBAR_CONFIG.typography.size} 
              ${NAVBAR_CONFIG.typography.lineHeight} 
              ${NAVBAR_CONFIG.typography.tracking} 
              ${NAVBAR_CONFIG.transitions}
              ${isActive ? NAVBAR_CONFIG.typography.weight.bold : NAVBAR_CONFIG.typography.weight.normal}
            `}
          >
            {item.name}
          </button>
        </Link>
      );
    });
  }, [navHeaderRoutes, currentLocation.pathname]);

  // Function to render the content within the expandable side-menu
  const buildSideMenuContent = useCallback(() => {
    return sideMenuRoutes.map((item: Route, index: number) => {
      const isActive = currentLocation.pathname === item.path;
      return (
        <Link
          to={item.path}
          key={index}
          aria-current={isActive ? "page" : undefined}
        >
          <div
            className={`${NAVBAR_CONFIG.colors.accent} font-semibold hover:bg-white/10 w-full py-4 px-4 ${isActive ? "bg-white/5" : ""}`}
          >
            {item.name}
          </div>
        </Link>
      );
    });
  }, [sideMenuRoutes, currentLocation.pathname]);

  if (
    !isAuthenticated(authStatus) ||
    !canAccessParticipantWebapp ||
    loadingAgency
  ) {
    return null;
  }

  return (
    <>
      <nav
        className={`navbar ${NAVBAR_CONFIG.height} rounded-b-sm ${NAVBAR_CONFIG.colors.background} ${NAVBAR_CONFIG.shadow} px-0 py-0 fixed top-0 left-0 right-0 z-50`}
      >
        <div className="drawer">
          <input id="my-drawer-3" type="checkbox" className="drawer-toggle" />
          <div className="drawer-content flex flex-col">
            {/* Navbar Drawer Button - Visible when screen width is small */}
            <div className="w-full flex-1 navbar px-0 mx-0">
              <div className="flex-none md:hidden">
                {/* Drawer Open Button (When device width is small, such as on mobile devices) */}
                <label
                  htmlFor="my-drawer-3"
                  aria-label="open sidebar"
                  className={`btn btn-square btn-ghost ${NAVBAR_CONFIG.colors.text} pr-0 focus:ring-2 focus:ring-white/50`}
                  onClick={toggleSideMenu}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    className="inline-block w-6 h-6 stroke-current"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </label>
              </div>

              {/* RePath Logo */}
              <div className="px-0 ml-6 mr-16 z-0">
                <img
                  src="/repath-logo-vector.svg"
                  className="h-10"
                  alt="RePath logo"
                />
              </div>

              <div className="flex-1 w-full flex justify-between">
                {/* Page Navigation buttons */}
                <div className="flex items-center">
                  {/* Nav buttons will hide when screen width becomes too small to fit on one line */}
                  <div
                    className={`hidden md:flex md:justify-center ${NAVBAR_CONFIG.spacing.navGap}`}
                  >
                    {/* Navbar menu content rendered here, using the defined routes object */}
                    {configureNavigationHeader()}
                  </div>
                </div>

                {/* Sign Out button - always in the top right corner */}
                <div className="pr-10 pl-3">
                  <button
                    onClick={handleSignOut}
                    className={`
                       flex items-center justify-center 
                       ${SIGN_OUT_BUTTON_CONFIG.width} ${SIGN_OUT_BUTTON_CONFIG.height} ${SIGN_OUT_BUTTON_CONFIG.minWidth}
                       ${SIGN_OUT_BUTTON_CONFIG.borderRadius} ${SIGN_OUT_BUTTON_CONFIG.border} 
                       p-2 gap-1 ${SIGN_OUT_BUTTON_CONFIG.background} ${SIGN_OUT_BUTTON_CONFIG.textColor}
                       ${SIGN_OUT_BUTTON_CONFIG.shadow} transition-all duration-200 ease-in-out
                       ${SIGN_OUT_BUTTON_CONFIG.hoverShadow} focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-primary normal-case 
                       ${SIGN_OUT_BUTTON_CONFIG.fontSize} ${SIGN_OUT_BUTTON_CONFIG.fontWeight}
                     `}
                    aria-label="Sign out of your account"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {strings.signOut}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 
            Side Navigation Menu 
            - Button to open the side menu is visible when screen width is small
            - If the menu is not open, the width is set to zero to prevent the content overlay from receiving clicks
          */}
          <div
            className={`
              fixed inset-0 bg-black bg-opacity-50 transition-opacity 
              ${sideMenuExpanded ? "opacity-100" : "opacity-0"}
              ${sideMenuExpanded ? "w-full" : "w-0"}
              ${sideMenuExpanded ? "z-50" : "z-0"}
            `}
            onClick={toggleSideMenu}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-menu-title"
          >
            <div
              className={`
                fixed inset-y-0 left-0 ${MOBILE_DRAWER_CONFIG.width} ${MOBILE_DRAWER_CONFIG.background} rounded-r-lg shadow-lg
                transform transition-transform 
                ${sideMenuExpanded ? "translate-x-0" : "-translate-x-full"}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`grid grid-cols-1 ${MOBILE_DRAWER_CONFIG.divider}`}
              >
                <h2 id="mobile-menu-title" className="sr-only">
                  Navigation menu
                </h2>
                {buildSideMenuContent()}
                {/* Sign Out option in mobile menu */}
                <div
                  className={`${NAVBAR_CONFIG.colors.accent} font-semibold hover:bg-white/10 w-full py-4 px-4`}
                >
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-1"
                  >
                    {strings.signOut}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar;
